import { noop } from 'lodash-es'
import React from 'react'

import { boxShadowHover } from '@/_vars'
import { useOnClickOutsideOnDesktop } from '@/lib/react-hooks/generic-hooks'
import { AttachDirection, Coords } from '@/lib/utils'
import { white } from '@/refactor/colors'
import { css } from '@/styles'
import styled, { AnimationNames, EnterAnim, EnterAnimProps } from '@/styles'
import { DefaultProps } from '@/travelsuit'

export interface IProps extends DefaultProps {
	attachTo?: AttachDirection
	attachOffset?: Partial<Coords>
	animation?: AnimationNames
	animationDelay?: number
	animationDuration?: number
	animationEasing?: React.CSSProperties['animationTimingFunction']
	background?: boolean | string
	dropShadow?: boolean | string
	onClickOut?(e: React.MouseEvent<HTMLElement>): void
}

interface ContainerProps {
	shadow?: IProps['dropShadow']
	background?: IProps['background']
}

const containerStyle = css<ContainerProps>`
	position: absolute;
	border-radius: 5px;

	${({ shadow }) => (shadow ? `box-shadow: ${shadow === true ? boxShadowHover : shadow};` : '')}

	${({ background }) => (background ? `background: ${background === true ? white : background};` : '')}
`

const Container = styled.div<ContainerProps>`
	${containerStyle}
`
export const AnimatedContainer = styled(EnterAnim)<ContainerProps & EnterAnimProps>`
	${containerStyle}
`

function getOffset(attachOffset?: Partial<Coords>): Coords {
	return { ...attachOffset, x: attachOffset?.x || 0, y: attachOffset?.y || 0 }
}

function attachDirectionStyle(attachTo?: AttachDirection, attachOffset?: Partial<Coords>): React.CSSProperties {
	const offset = getOffset(attachOffset)

	if (!attachTo) {
		return {
			top: offset.y,
			left: offset.x,
		}
	}

	return {
		top: attachTo.top ? offset.y : undefined,
		bottom: attachTo.bottom ? offset.y : undefined,
		right: attachTo.right ? offset.x : undefined,
		left: attachTo.left ? offset.x : undefined,
	}
}

function attachTransformOrigin(attachTo?: AttachDirection): React.CSSProperties {
	if (!attachTo) {
		return {}
	}

	const horiz = attachTo.left ? 'left' : attachTo.right ? 'right' : 'center'
	const vert = attachTo.top ? 'top' : attachTo.bottom ? 'bottom' : 'center'

	return {
		transformOrigin: `${horiz} ${vert}`,
	}
}

export const DEFAULT_ANIMATION_DURATION = 150

function FloatingContainer(props: IProps) {
	const {
		className,
		children,
		attachTo,
		attachOffset,
		animation,
		animationDelay,
		animationDuration = DEFAULT_ANIMATION_DURATION,
		animationEasing,
		dropShadow,
		background,
		style,
	} = props

	const finalStyle = { ...attachDirectionStyle(attachTo, attachOffset), ...attachTransformOrigin(attachTo), ...style }

	const containerRef = React.useRef<HTMLDivElement | null>(null)
	useOnClickOutsideOnDesktop({ containerRef, onClickOutside: props.onClickOut ?? noop })

	if (!animation) {
		return (
			<Container
				className={className}
				shadow={dropShadow}
				background={background}
				style={finalStyle}
				ref={containerRef}
			>
				{children}
			</Container>
		)
	}

	return (
		<AnimatedContainer
			ref={containerRef}
			className={className}
			animation={animation}
			delay={animationDelay}
			duration={animationDuration}
			easing={animationEasing}
			shadow={dropShadow}
			background={background}
			style={finalStyle}
		>
			{children}
		</AnimatedContainer>
	)
}

export default FloatingContainer
